import React, { useMemo, useState, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import ReactSelect from "react-select";
import AsyncSelect, { useAsync } from 'react-select/async';
import Select from 'react-select';

import { useGetDisciplineListQuery, useLazyGetDisciplineListQuery } from "store/services/disciplines";
import { cloneDeep, debounce } from "lodash";
import { FieldArray, Formik } from "formik";
import { useCreateGoalMutation, useDeleteGoalContextMutation, useGetGoalDataQuery, useUpdateGoalMutation } from "store/services/goals";
import goalFormSchema from "./GoalForm.schema";
import AppInputField from "components/shared/FormComponents/AppInputField";
import LoadingButton from "components/shared/LoadingButton";
import { GOALS_LIST } from "config/paths";
import { successMessageToast } from "helpers/common";
import { useNavigate, useParams, Link } from "react-router-dom";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { PlusIcon, MinusIcon } from "Assets/icons/";

// css
import styles from "./addForm.module.scss";
import ErrorComponent from "components/shared/ErrorComponent";
import ConfirmationPop from "components/Modals/ConfirmationPop";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { RequiredFieldAstrick } from "components/Common/Common";


const AddGoals = ({ }) => {
  const { id: goalId } = useParams();
  const editMode = !!goalId;
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [initialValues, setInitialValues] = useState({
    discipline_id: null,
    discipline_area: null,
    title: "",
    goal_contexts: [{
      id: null,
      context: ""
    }],
    status: "active"
  });

  const [deleteContext, { isLoading: deleteLoading }] = useDeleteGoalContextMutation();

  const { data: disciplineList, isLoading: disciplineListLoading } = useGetDisciplineListQuery({});
  const disciplineListOptions = disciplineList?.records?.map(x => ({ label: x.name, value: x.id, discipline_areas: x.discipline_areas }))
  const { data: goalData, isLoading: getGoalLoading, isError: getGoalError } = useGetGoalDataQuery(goalId, { skip: !goalId });
  const [createGoal, { isLoading }] = useCreateGoalMutation();
  const [updateGoal, { isLoading: updateGoalLoading }] = useUpdateGoalMutation();

  const disciplineAreas = useMemo(() => {
    if (selectedDiscipline?.discipline_areas) {
      return selectedDiscipline?.discipline_areas.map((area) => ({
        value: area.id,
        label: area.name
      }));
    }
  }, [selectedDiscipline]);

  useEffect(() => {
    if (!goalData) {
      return;
    }
    setInitialValues(prevState => ({
      ...prevState,
      discipline_id: goalData?.discipline_id,
      discipline_area: goalData?.discipline_area,
      title: goalData?.title,
      goal_contexts: goalData?.goal_contexts,
      status: goalData?.status
    }))
  }, [goalData]);

  const loadDisciplineProfileOptions = debounce(async (inputValue, callback) => {
    const result = await trigger({ search: inputValue }).unwrap();

    const options = result?.records?.map((profile) => ({
      value: profile.id,
      discipline_areas: profile.discipline_areas,
      label: profile.name,
    })) || [];

    callback(options);
  }, 500);

  const handleSubmit = async (values) => {
    try {
      const payload = cloneDeep(values);
      let goalContexts = payload.goal_contexts;
      if (!editMode) {
        goalContexts =
          payload.goal_contexts
            .map((context) => context.context)
            .filter(context => !!context);
      }

      payload.goal_contexts = goalContexts;

      let result;
      if (editMode) {
        result = await updateGoal({
          id: goalId,
          payload
        });
      } else {
        result = await createGoal(payload);
      }
      if (result.error) {
        console.error("Error creating Goal", result.error);
      } else {
        successMessageToast(result.data?.message || "Goal created successfully");
        navigate(GOALS_LIST);
      }
    } catch (err) {
      console.log("Error creating Goal", err);
    }
  }

  const handleClickCancel = () => {
    navigate(GOALS_LIST);
  }

  const handleDelete = async () => {
    try {

      const result = await deleteContext(currentEntry?.id);
      if (!result?.error) successMessageToast(result?.data?.message);

    } catch (err) {
      console.error("error deleting goal context", err);
    };
  };

  return (
    <>
      <ConfirmationPop
        confirmation={showModal}
        setConfirmation={setShowModal}
        confirmHandler={async () => {

          setShowModal(false)
          await handleDelete()
        }}
        type="delete"
      />
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="pageHead pb-3 d-flex align-items-center">
                <Link
                  className="themeBlue fw-sbold back-to-btn"
                  to="/manage-goals/"
                >
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                  </svg> Back
                </Link>
                <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit" : "Create New"} Goal</h6>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              {getGoalLoading ? (
                <LoadingIndicator />
              ) : getGoalError ? (
                <ErrorComponent />
              ) : deleteLoading ? (
                <LoadingOverlay />
              ) : (
                <Formik
                  initialValues={initialValues}
                  validationSchema={goalFormSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    handleSubmit,
                    isSubmitting,
                    values,
                    errors,
                    setFieldValue
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      {console.log("values >>>", values, errors)}
                      <div
                        className="py-3"
                        style={{ borderTop: "1px solid #41a2a84d" }}
                      >
                        <Row>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Select Discipline
                              <RequiredFieldAstrick />
                            </label>
                            <Select
                              value={disciplineListOptions?.find(x => x.value == values?.discipline_id)}
                              options={disciplineListOptions}
                              isLoading={disciplineListLoading}
                              onChange={(discipline) => {
                                setSelectedDiscipline(discipline);
                                setFieldValue("discipline_id", discipline?.value || null);
                              }}
                            />
                            {/* <AsyncSelect
                              cacheOptions
                              options={disciplineListOptions}
                              defaultValue={{
                                value: goalData?.discipline_id,
                                label: goalData?.discipline_goals.name
                              }}
                              loadOptions={loadDisciplineProfileOptions}
                              onChange={(discipline) => {
                                setSelectedDiscipline(discipline);
                                setFieldValue("discipline_id", discipline?.value || null);
                              }}
                            /> */}
                          </Col>
                          {console.log("disciplineAreasdisciplineAreas", disciplineAreas)}
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Select Area
                              <RequiredFieldAstrick />
                            </label>
                            <ReactSelect
                              defaultValue={{
                                value: goalData?.discipline_area,
                                label: goalData?.discipline_area_goals.name
                              }}
                              options={selectedDiscipline?.discipline_areas?.map(i => ({ value: i.id, label: i.name }))}
                              onChange={(area) => {
                                setFieldValue("discipline_area", area?.value || null);
                              }}
                            />
                          </Col>
                          <Col lg="12" className="my-2">
                            <Row>
                              <Col lg="5" sm="6" className="my-2">
                                <div className="py-2">
                                  <label
                                    htmlFor=""
                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                  >
                                    Add new goal
                                    <RequiredFieldAstrick />
                                  </label>
                                  <AppInputField
                                    name="title"
                                    type="text"
                                    placeholder="Goal title here"
                                    className={`${styles.formControl} form-control`}
                                  />
                                </div>
                                <div className="py-2">
                                  <label
                                    htmlFor=""
                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                  >
                                    Add Context
                                    <RequiredFieldAstrick />
                                  </label>
                                  <FieldArray name="goal_contexts">
                                    {({ insert, remove, push }) => (
                                      <ul className="list-unstyled ps-0 mb-0">
                                        {values.goal_contexts.length > 0 &&
                                          values.goal_contexts.map((area, index) => (
                                            <GoalContextField
                                              isLastItem={index === values.goal_contexts.length - 1}
                                              index={index}
                                              insert={insert}
                                              remove={remove}
                                              push={push}
                                              value={area}
                                              setConfirmation={setShowModal}
                                              setCurrentEntry={setCurrentEntry}
                                              values={values?.goal_contexts || []}
                                            />
                                          ))}
                                      </ul>
                                    )}
                                  </FieldArray>
                                </div>
                              </Col>
                              {/* <Col lg="2" md="3" sm="4" className="my-2">
                                <Button
                                  style={{ background: "#F3F3F3" }}
                                  className="h-100 d-flex themeBlue align-items-center border-light w-100 justify-content-center flex-column py-3"
                                >
                                  <span className="icn mb-2">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="32"
                                      height="32"
                                      viewBox="0 0 32 32"
                                      fill="none"
                                    >
                                      <mask
                                        id="mask0_0_12941"
                                        maskUnits="userSpaceOnUse"
                                        x="0"
                                        y="0"
                                        width="32"
                                        height="32"
                                        style={{ maskType: "alpha" }}
                                      >
                                        <rect
                                          width="32"
                                          height="32"
                                          fill="#D9D9D9"
                                        ></rect>
                                      </mask>
                                      <g mask="url(#mask0_0_12941)">
                                        <path
                                          d="M14.6667 17.3337V21.3337C14.6667 21.7114 14.7945 22.0281 15.0501 22.2837C15.3056 22.5392 15.6223 22.667 16.0001 22.667C16.3779 22.667 16.6945 22.5392 16.9501 22.2837C17.2056 22.0281 17.3334 21.7114 17.3334 21.3337V17.3337H21.3334C21.7112 17.3337 22.0279 17.2059 22.2834 16.9503C22.539 16.6948 22.6667 16.3781 22.6667 16.0003C22.6667 15.6225 22.539 15.3059 22.2834 15.0503C22.0279 14.7948 21.7112 14.667 21.3334 14.667H17.3334V10.667C17.3334 10.2892 17.2056 9.97255 16.9501 9.71699C16.6945 9.46144 16.3779 9.33366 16.0001 9.33366C15.6223 9.33366 15.3056 9.46144 15.0501 9.71699C14.7945 9.97255 14.6667 10.2892 14.6667 10.667V14.667H10.6667C10.289 14.667 9.9723 14.7948 9.71675 15.0503C9.46119 15.3059 9.33342 15.6225 9.33342 16.0003C9.33342 16.3781 9.46119 16.6948 9.71675 16.9503C9.9723 17.2059 10.289 17.3337 10.6667 17.3337H14.6667ZM16.0001 29.3337C14.1556 29.3337 12.4223 28.9837 10.8001 28.2837C9.17786 27.5837 7.76675 26.6337 6.56675 25.4337C5.36675 24.2337 4.41675 22.8225 3.71675 21.2003C3.01675 19.5781 2.66675 17.8448 2.66675 16.0003C2.66675 14.1559 3.01675 12.4225 3.71675 10.8003C4.41675 9.1781 5.36675 7.76699 6.56675 6.56699C7.76675 5.36699 9.17786 4.41699 10.8001 3.71699C12.4223 3.01699 14.1556 2.66699 16.0001 2.66699C17.8445 2.66699 19.5779 3.01699 21.2001 3.71699C22.8223 4.41699 24.2334 5.36699 25.4334 6.56699C26.6334 7.76699 27.5834 9.1781 28.2834 10.8003C28.9834 12.4225 29.3334 14.1559 29.3334 16.0003C29.3334 17.8448 28.9834 19.5781 28.2834 21.2003C27.5834 22.8225 26.6334 24.2337 25.4334 25.4337C24.2334 26.6337 22.8223 27.5837 21.2001 28.2837C19.5779 28.9837 17.8445 29.3337 16.0001 29.3337Z"
                                          fill="#B46C9F"
                                        ></path>
                                      </g>
                                    </svg>
                                  </span>
                                  Create New Goal
                                </Button>
                              </Col> */}
                            </Row>
                          </Col>
                        </Row>
                      </div>
                      <Row className="mt-4">
                        <Col className="my-2" lg="12">
                          <div
                            className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                            style={{ borderTop: "1px solid #41a2a84d" }}
                          >
                            <LoadingButton
                              type="submit"
                              className="d-flex align-items-center justify-content-center commonBtn"
                              isLoading={isLoading || updateGoalLoading}
                            >
                              Save
                            </LoadingButton>
                            <Button
                              className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                              onClick={handleClickCancel}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

const GoalContextField = ({ index, isLastItem, remove, push, value, setConfirmation, setCurrentEntry, values }) => {
  return (
    <>
      <li key={index}>
        <div className="py-1 d-flex align-items-center gap-10">
          <AppInputField
            name={`goal_contexts.${index}.context`}
            placeholder="Context Name"
            className={`${styles.formControl} form-control`}
          />
          <Button
            className="border-0 p-0"
            variant="transparnet"
          >
            {isLastItem ? (

              values?.length > 1 ? (
                <div className="d-flex w-100">
                  <PlusIcon onClick={() => push({ id: null, context: "" })} />
                  <MinusIcon onClick={() => {
                    if (value?.id) {
                      setCurrentEntry(value);
                      setConfirmation(true);
                      return;
                    }
                    remove(index)
                  }} />
                </div>
              ) : <PlusIcon onClick={() => push({ id: null, context: "" })} />

            ) : <MinusIcon onClick={() => {
              if (value?.id) {
                setCurrentEntry(value);
                setConfirmation(true);
                return;
              }
              remove(index)
            }} />
            }
          </Button>
        </div>
      </li>
    </>
  )
}

export default AddGoals;
